"use client"

import React, { useRef, useState } from "react"
import { MAX_ADULTS, useTravelComparatorStore } from "@/app/stores/travel/travel-comparator.store"
import { useClickOutside } from "@/app/hooks/useClickOutside"
import Stepper from "@/app/components/atomic/Stepper"
import { ADULT_AGE, ageFromIsoBirthdate, MAX_TRAVELER_AGE, today } from "../travelFormConfig"
import TripFieldCard from "./TripFieldCard"

// Âge saisi sur la landing → date de naissance (le back tarife par âge/date de naissance).
function ageToBirthdate(age: number): string {
    const [year, month, day] = today().split("-").map(Number)
    return `${year - age}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`
}

function birthdateToAge(iso: string): string {
    const age = ageFromIsoBirthdate(iso)
    return age !== null && age >= 0 ? String(age) : ""
}

const plural = (count: number, noun: string) => `${count} ${noun}${count > 1 ? "s" : ""}`

// Adults and children are only told apart by age, so the split needs every age to be filled in
function travelersSummary(travelers: { birthdate: string }[]): string | null {
    const ages = travelers.map((traveler) => ageFromIsoBirthdate(traveler.birthdate))
    if (ages.every((age) => age === null)) {
        return null
    }
    if (ages.some((age) => age === null)) {
        return plural(travelers.length, "voyageur")
    }
    const children = ages.filter((age) => age !== null && age < ADULT_AGE).length
    const adults = travelers.length - children
    return [adults > 0 && plural(adults, "adulte"), children > 0 && plural(children, "enfant")].filter(Boolean).join(", ")
}

const travelerName = (index: number) => (index === 0 ? "Principal" : `Voyageur ${index + 1}`)

export default function TravelersCell() {
    const { form, setAdults, setTravelerBirthdate } = useTravelComparatorStore()
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)

    useClickOutside(ref, () => setOpen(false))

    return (
        <div ref={ref} className="relative min-w-0">
            <TripFieldCard
                label="Voyageurs"
                value={travelersSummary(form.travelers)}
                placeholder="Qui voyage ?"
                open={open}
                onToggle={() => setOpen((current) => !current)}
                panelClassName="left-auto right-0 w-[300px] max-w-[85vw] p-4"
            >
                <p className="text-[14px] font-semibold text-pale">Nombre de voyageurs</p>
                <div className="mt-2">
                    <Stepper
                        value={form.adults}
                        min={1}
                        max={MAX_ADULTS}
                        onChange={setAdults}
                        decrementLabel="Retirer un voyageur"
                        incrementLabel="Ajouter un voyageur"
                    />
                </div>

                <p className="mt-4 text-[14px] font-semibold text-pale">Âge des voyageurs</p>
                <div className="mt-2 space-y-2">
                    {form.travelers.map((traveler, index) => (
                        <div key={index} className="flex items-center justify-between gap-3">
                            <span className="text-[14px] text-pale">{travelerName(index)}</span>
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    min={0}
                                    max={MAX_TRAVELER_AGE}
                                    inputMode="numeric"
                                    placeholder="Âge"
                                    value={birthdateToAge(traveler.birthdate)}
                                    onChange={(event) => {
                                        const age = Number(event.target.value)
                                        setTravelerBirthdate(index, event.target.value !== "" && age >= 0 ? ageToBirthdate(age) : "")
                                    }}
                                    className="h-9 w-16 rounded-lg border border-rm-gray-light px-2 text-center text-[14px] text-pale outline-none focus:border-rm-blue"
                                />
                                <span className="text-[13px] text-[#6b7280]">ans</span>
                            </div>
                        </div>
                    ))}
                </div>
            </TripFieldCard>
        </div>
    )
}
